import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../ApiContext'; 
import toast from 'react-hot-toast';
import './Gallery.css'; 

const GALLERY_ENDPOINT = '/gallery';

const Gallery = ({ onSelectImage, onClose }) => {
    
    const { api } = useApi();
    
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    // --- Fetch Images (READ) ---
    const fetchImages = useCallback(async () => {
        if (!api) return;
        setLoading(true);
        try {
            const response = await api.get(GALLERY_ENDPOINT);
            setImages(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching gallery images:', err);
            setError('Failed to load images from the server.');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    // --- Upload Image (CREATE) ---
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setUploadFile(file);
    };

    const handleUpload = async (event) => {
        event.preventDefault();

        if (!uploadFile || !api) {
            toast.error('Please select a file to upload.');
            return;
        }

        const formData = new FormData();
        formData.append('imageFile', uploadFile); 

        setUploading(true);
        try {
            await api.post(GALLERY_ENDPOINT, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            toast.success("Image uploaded successfully!");
            
            setUploadFile(null);
            document.getElementById('file-upload-input').value = ''; 
            await fetchImages();

        } catch (err) {
            console.error('Error uploading image:', err.response || err);
            const errorMessage = err.response?.data?.message || err.message || 'An unknown error occurred during upload.';
            toast.error(`Upload failed: ${errorMessage}`);
        } finally {
            setUploading(false);
        }
    };

    // --- Delete Image (DELETE) ---
    const handleDelete = async (id, name) => {
        if (!api || !window.confirm(`Are you sure you want to delete "${name}"? This action is permanent.`)) {
            return;
        }

        try {
            await api.delete(`${GALLERY_ENDPOINT}/${id}`); 
            toast.success(`"${name}" deleted.`);
            setImages(prevImages => prevImages.filter(img => img.id !== id));
        } catch (err) {
            console.error(`Error deleting image ${id}:`, err.response || err);
            const errorMessage = err.response?.data?.message || err.message || 'An unknown error occurred during deletion.';
            toast.error(`Deletion failed: ${errorMessage}`);
        }
    };

    // --- Image Card Component ---
    const ImageItem = ({ image }) => {
        const displayName = image.name.length > 25 ? image.name.substring(0, 22) + '...' : image.name;

        return (
            <div className="GalleryModal-image-item-wrapper">
                <div 
                    className="GalleryModal-image-card" 
                    onClick={() => onSelectImage(image.file_path)} 
                >
                    <img 
                        src={image.file_path} 
                        alt={image.name} 
                        className="GalleryModal-gallery-image" 
                    />
                    <button 
                        className="GalleryModal-delete-icon-button" 
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            handleDelete(image.id, image.name); 
                        }}
                        title={`Delete ${image.name}`}
                    >
                        &times;
                    </button>
                </div>
                <span className="GalleryModal-image-name-below" title={image.name}>{displayName}</span>
            </div>
        );
    };

    // --- RENDERING ---
    if (loading) {
        return (
            <div className="GalleryModal-overlay">
                <div className="GalleryModal-content">
                    <h3>Loading Gallery...</h3>
                </div>
            </div>
        );
    }

    return (
        <div className="GalleryModal-overlay">
            <div className="GalleryModal-content">
                <button type="button" className="GalleryModal-close-button" onClick={onClose}>&times;</button>
                <h3 className="GalleryModal-title">Logo & Image Gallery Management 🖼️</h3>

                <form onSubmit={handleUpload} className="GalleryModal-upload-form">
                    <div className="GalleryModal-file-input-group">
                        <label htmlFor="file-upload-input" className="GalleryModal-file-label">
                            {uploadFile ? `Selected: ${uploadFile.name}` : 'Choose Image (JPG, PNG, WebP)'}
                        </label>
                        <input 
                            type="file" 
                            id="file-upload-input"
                            accept="image/jpeg,image/png,image/webp" 
                            onChange={handleFileChange} 
                            disabled={uploading}
                            style={{ display: 'none' }}
                        />
                        <button 
                            type="submit" 
                            className="GalleryModal-upload-button" 
                            disabled={uploading || !uploadFile}
                        >
                            {uploading ? 'Uploading...' : 'Upload & Add'}
                        </button>
                    </div>
                    <small className="GalleryModal-upload-note">Max file size: 5MB.</small>
                </form>

                {error && <p className="GalleryModal-error-message">{error}</p>}

                <div className="GalleryModal-image-grid-container">
                    {images.length === 0 ? (
                        <p className="GalleryModal-empty-gallery-message">No images in the gallery. Please upload one!</p>
                    ) : (
                        images.map(image => (
                            <ImageItem key={image.id} image={image} />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default Gallery;